import { Fragment, useMemo } from 'react';

import { Text, Tooltip, useTheme } from '@Sage/hoi-poi-ui';
import { getBackendBoolean } from '@Sage/ssm-utils/backendScalars';

import ReadOnlyIndicator from 'components/ReadOnlyIndicator';
import { ACTIVITIES, COMPANIES, CONTACTS, OPPORTUNITIES } from 'constants/Entities';
import OpportunityChip from 'containers/components/chips/OpportunityChip';
import { InfoRow, TitleRow } from 'containers/components/EntityDetailV2';
import { ACTIVITY_TYPE_EMAIL } from 'models/ActivityModel';

import SubtitleContent from '../../shared/SubtitleContent';
import SubtitleContentMultiple from '../../shared/SubtitleContentMultiple';
import TimezonePopover from '../../shared/TimezonePopover';
import TitleContentDetail from '../../shared/TitleContentDetail';
import { getTimezoneInfo } from '../../../utils/timeZone';

const useActivityDetailSharedContent = ({
    activity,
    canEditActivity,
    getDateText,
    getElapsedDateText,
    getHeaderItems,
    hasTabs,
    isFromWidgetDetail,
    labels,
    openTab,
    showActionsAndMenu,
}) => {
    const theme = useTheme();

    return useMemo(() => {
        const activityType = parseInt(activity?.ActivityType, 10);

        const timezoneInfo = getTimezoneInfo({
            activityDate: activity.ActivityDate,
            idActivityTimezone: activity.IdTimeZone,
        });

        const rightContent = (
            <Fragment>
                <Tooltip content={getElapsedDateText(activity.ActivityDate)} placement="left">
                    <Text
                        className="title-row__date"
                        type="body"
                        color={theme.colors.utility.textSecondary}
                        isTruncated
                    >
                        {getDateText(activity.ActivityDate)}
                    </Text>
                </Tooltip>
                {timezoneInfo && <TimezonePopover timezoneInfo={timezoneInfo} placement="left" />}
            </Fragment>
        );

        const companyContent = activity.IdCompany && activity.CompanyName && (
            <SubtitleContent
                id={activity.IdCompany}
                value={activity.CompanyName}
                entity={COMPANIES}
            />
        );

        let contactContent = null;

        if (activity.IDContact && activity.ContactName) {
            contactContent = (
                <SubtitleContent
                    key={activity.IDContact}
                    id={activity.IDContact}
                    value={activity.ContactName}
                    entity={CONTACTS}
                />
            );
        } else if (activity.UserName && activityType === ACTIVITY_TYPE_EMAIL) {
            contactContent = <Text key={activity.IDContact}>{activity.UserName}</Text>;
        }

        let multipleContactsContent = [];

        if (activity?.ContactIdList?.length && activity?.ContactList?.length) {
            const newContactIdList = activity.ContactIdList.split(';');
            newContactIdList.pop();
            const newContactList = activity.ContactList.split(';');
            newContactList.pop();

            multipleContactsContent = newContactIdList.reduce((arr, current, index) => {
                if (!arr.length) {
                    arr.push(contactContent);
                }
                arr.push(
                    <SubtitleContent
                        key={current}
                        id={current}
                        value={newContactList[index]}
                        entity={CONTACTS}
                    />,
                );
                return arr;
            }, []);
        }

        const finalContactContent = multipleContactsContent?.length
            ? multipleContactsContent
            : contactContent;

        let emailCompanyContent = null;

        if (activity?.Accounts?.length) {
            if (activity.Accounts.length > 1) {
                emailCompanyContent = (
                    <SubtitleContentMultiple accounts={activity.Accounts} entity={COMPANIES} />
                );
            } else {
                const finalAccount = activity.Accounts[0];
                let accountName = `${finalAccount.Name}`;
                if (finalAccount.Surname) accountName = `${accountName} ${finalAccount.Surname}`;
                emailCompanyContent = (
                    <SubtitleContent id={finalAccount.Id} value={accountName} entity={COMPANIES} />
                );
            }
        }

        let emailContactContent = null;
        let emailRole = '';

        if (activity?.Contacts?.length) {
            if (activity.Contacts.length > 1) {
                emailContactContent = (
                    <SubtitleContentMultiple
                        recipients={activity.Recipients}
                        contacts={activity.Contacts}
                        users={activity.Users}
                        entity={CONTACTS}
                    />
                );
            } else {
                const finalContact = activity.Contacts[0];
                emailRole = finalContact.Role;
                let contactName = `${finalContact.Name}`;
                if (finalContact.Surname) contactName = `${contactName} ${finalContact.Surname}`;
                emailContactContent = (
                    <SubtitleContent id={finalContact.Id} value={contactName} entity={CONTACTS} />
                );
            }
        } else if (activity.UserName && activityType === ACTIVITY_TYPE_EMAIL) {
            emailContactContent = <Text>{activity.UserName}</Text>;
        }

        let leftContent = <TitleContentDetail label={activity.Type} />;

        const idOpportunity = activity.IDExpediente || activity.IdOpportunity;
        const nameOpportunity = activity.Expediente || activity.OpportunityName;

        const opportunityRow = idOpportunity && idOpportunity !== '-1' && idOpportunity !== '0' && (
            <InfoRow
                label={labels.opportunity}
                content={
                    <OpportunityChip
                        idOpportunity={idOpportunity}
                        nameOpportunity={nameOpportunity}
                        remotePopover={true}
                        onClickPopover={
                            hasTabs
                                ? () =>
                                      openTab({
                                          entity: OPPORTUNITIES,
                                          id: idOpportunity,
                                          hasCrudInDetail: true,
                                      })
                                : null
                        }
                    />
                }
            />
        );

        let emailOpportunityRow = null;

        if (activity?.Opportunity) {
            emailOpportunityRow = (
                <InfoRow
                    label={labels.opportunity}
                    content={
                        <OpportunityChip
                            idOpportunity={activity.Opportunity.Id}
                            nameOpportunity={activity.Opportunity.Name}
                            remotePopover={true}
                            onClickPopover={
                                hasTabs
                                    ? () =>
                                          openTab({
                                              entity: OPPORTUNITIES,
                                              id: activity.Opportunity.Id,
                                              hasCrudInDetail: true,
                                          })
                                    : null
                            }
                        />
                    }
                />
            );
        }

        const { leftContent: newLeftContent, actionsAndMenu } = getHeaderItems({
            activity,
            isFromWidgetDetail,
        });

        leftContent = newLeftContent || leftContent;

        const metadata = {
            audit: {
                created: {
                    date: activity.audit?.created?.date,
                    user: {
                        name: activity.audit?.created?.user?.name,
                    },
                },
                modified: {
                    date: activity.audit?.modified?.date,
                    user: {
                        name: activity.audit?.modified?.user?.name,
                    },
                },
                readOnlyDb: activity.audit?.readOnlyDb,
            },
            extId: activity.ExtId,
            id: activity.Id,
            importDate: activity.ImportDate,
            isPrivate: getBackendBoolean(activity.IsPrivate),
            level: activity.intSecurityLevel,
        };

        const isActivityReadOnly = canEditActivity && getBackendBoolean(activity.IsReadOnly);

        const titleRow = (
            <TitleRow
                leftContent={leftContent}
                rightContent={showActionsAndMenu && actionsAndMenu}
                metadata={metadata}
                entity={ACTIVITIES}
                readOnlyIndicator={<ReadOnlyIndicator isReadOnly={isActivityReadOnly} />}
            />
        );

        return {
            companyContent,
            emailCompanyContent,
            emailContactContent,
            emailOpportunityRow,
            emailRole,
            finalContactContent,
            opportunityRow,
            rightContent,
            titleRow,
        };
    }, [
        activity,
        canEditActivity,
        getDateText,
        getElapsedDateText,
        getHeaderItems,
        hasTabs,
        isFromWidgetDetail,
        labels.opportunity,
        openTab,
        showActionsAndMenu,
        theme.colors.utility.textSecondary,
    ]);
};

export default useActivityDetailSharedContent;
